/** 화면 조각들. 문구는 한국어, 값은 canonical. */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { formatTime } from '@/format'

export function Card({ title, actions, children }: {
  title?: string
  /** 제목 줄 오른쪽 버튼(회원 상세의 정지·등급 변경·퇴출 — 명세 EA-07). */
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="rounded border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      {(title || actions) && (
        <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          {title && <h2 className="text-sm font-semibold">{title}</h2>}
          {actions && <div className="ml-auto flex flex-wrap gap-2">{actions}</div>}
        </div>
      )}
      <div className="p-4">{children}</div>
    </section>
  )
}

/** canonical 값을 보여 줄 때 쓴다 — 표시명 옆에 원문을 남긴다. */
export function Code({ children }: { children: ReactNode }) {
  return (
    <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
      {children}
    </code>
  )
}

/**
 * 코드 값 = 표시 이름 + 회색 원문(명세 0-6 '코드 값'). 표에 없는 새 코드는 원문만(4-2).
 */
export function Labeled({ labels, code }: {
  labels: Readonly<Record<string, string>>
  code: string | number | null | undefined
}) {
  if (code === null || code === undefined || code === '') return <Maybe value={null} />
  const label = labels[String(code)]
  return label === undefined
    ? <Code>{code}</Code>
    : <>{label} <Code>{code}</Code></>
}

export function Notice({ tone = 'info', children }: { tone?: 'info' | 'ok' | 'warn' | 'stop'; children: ReactNode }) {
  const cls = {
    info: 'border-sky-300 bg-sky-50 text-sky-900 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-100',
    ok: 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100',
    warn: 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100',
    stop: 'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100',
  }[tone]
  return <div className={`rounded border px-4 py-3 text-sm ${cls}`}>{children}</div>
}

/** 짧은 배지 — 「최고관리자」·「임시값」·「시행 중」 같은 표시. */
export function Badge({ tone = 'plain', children }: {
  tone?: 'plain' | 'warn' | 'stop' | 'ok'
  children: ReactNode
}) {
  const cls = {
    plain: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
    warn: 'bg-amber-100 text-amber-900 dark:bg-amber-900 dark:text-amber-100',
    stop: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100',
    ok: 'bg-teal-100 text-teal-900 dark:bg-teal-900 dark:text-teal-100',
  }[tone]
  return <span className={`ml-2 rounded px-1.5 py-0.5 text-xs ${cls}`}>{children}</span>
}

export function Button({
  children, onClick, type = 'button', disabled, tone = 'primary',
}: {
  children: ReactNode
  onClick?: (() => void) | undefined
  type?: 'button' | 'submit'
  // ⚠ `| undefined` 를 적는다 — exactOptionalPropertyTypes 아래에서는
  // 「생략해도 된다」와 「undefined 를 넘겨도 된다」가 다른 말이다.
  disabled?: boolean | undefined
  tone?: 'primary' | 'plain' | undefined
}) {
  const cls = tone === 'primary'
    ? 'bg-teal-800 text-white hover:bg-teal-700 disabled:bg-slate-300 dark:disabled:bg-slate-700'
    : 'border border-slate-300 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800'
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      className={`rounded px-4 py-2 text-sm font-medium disabled:cursor-not-allowed ${cls}`}>
      {children}
    </button>
  )
}

export const INPUT = 'rounded border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800'

export function Field({ label, ...rest }: { label: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium">{label}</span>
      <input {...rest} className={INPUT} />
    </label>
  )
}

/** 여러 줄 입력 — 사유·근거·약관 본문. */
export function TextArea({ label, ...rest }: { label: string } & React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium">{label}</span>
      <textarea rows={3} {...rest} className={INPUT} />
    </label>
  )
}

/** 고르개 한 칸. 값은 canonical, 보이는 것은 표시 이름. */
export function Select({ label, value, onChange, options }: {
  label: string
  value: string
  onChange: (v: string) => void
  options: readonly { value: string; label: string }[]
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium">{label}</span>
      <select value={value} className={INPUT} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  )
}

/** 값이 없을 때 0 을 그리지 않는다 — 「모른다」와 「없다」는 다르다. */
export function Maybe({ value, unit }: { value: string | number | null | undefined; unit?: string }) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-slate-400" title="No value yet">—</span>
  }
  return <>{value}{unit ?? ''}</>
}

/** 시각(밀리초) — 없으면 —. */
export function Time({ ms }: { ms: number | null | undefined }) {
  return <Maybe value={formatTime(ms)} />
}

export const LINK = 'text-teal-700 underline dark:text-teal-400'

/**
 * 회원 링크 — 주소는 `/users/{memberId}`(명세 0-6 '회원 링크' 고침). 아이디가 없고 번호만
 * 오면 「회원 #번호」.
 */
export function MemberLink({ memberId, loginId }: {
  memberId: number | null | undefined
  loginId?: string | null | undefined
}) {
  if (memberId === null || memberId === undefined) return <Maybe value={loginId ?? null} />
  return (
    <Link className={LINK} to={`/users/${memberId}`}>
      {loginId ? loginId : `Member #${memberId}`}
    </Link>
  )
}

/**
 * 관리자 표시 — 계약이 번호만 주면 「관리자 #번호」, 아이디가 오면 아이디(명세 0-6, 7절 C-04).
 */
export function AdminRef({ adminId, loginId, empty }: {
  adminId: number | null | undefined
  loginId?: string | null | undefined
  /** 비었을 때 보일 말(없으면 —). */
  empty?: string
}) {
  if (loginId) return <>{loginId}</>
  if (adminId === null || adminId === undefined) {
    return empty ? <span className="text-slate-400">{empty}</span> : <Maybe value={null} />
  }
  return <>Admin #{adminId}</>
}

export const TH = 'px-3 py-2 text-left font-medium text-slate-500'
export const TD = 'px-3 py-2 border-t border-slate-100 dark:border-slate-800'
export const NUM = `${TD} font-mono tabular-nums`

/** 정의 목록 한 칸(왼쪽 이름 · 오른쪽 값). */
export function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

/** 탭 줄 — 탭 상태는 주소에 싣지 않는다(명세 2-7, 레거시 그대로). */
export function Tabs<K extends string>({ tabs, active, onSelect }: {
  tabs: readonly { kind: K; label: string }[]
  active: K
  onSelect: (k: K) => void
}) {
  return (
    <div className="mb-4 flex gap-1 border-b border-slate-200 dark:border-slate-700" role="tablist">
      {tabs.map((t) => (
        <button key={t.kind} type="button" role="tab" aria-selected={active === t.kind}
          onClick={() => onSelect(t.kind)}
          className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
            active === t.kind
              ? 'border-teal-800 text-teal-800 dark:border-teal-400 dark:text-teal-400'
              : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
          }`}>
          {t.label}
        </button>
      ))}
    </div>
  )
}
